
import React, { useState, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, Menu, X, LogOut, User, Smartphone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import SpotifyMusicPanel from "@/components/spotify/SpotifyMusicPanel";

const Dashboard = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userAvatar, setUserAvatar] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const user = data.session?.user;
      if (user) {
        setUserEmail(user.email ?? null);
        setUserAvatar(user.user_metadata?.avatar_url ?? null);
      }
    });
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast({ title: "Signed out" });
    navigate("/auth", { replace: true });
  };
  const location = useLocation();
  const isHome = location.pathname === "/dashboard";

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
      <header
        className="border-b border-border py-4 backdrop-blur-sm bg-background/80 sticky top-0 z-50"
        style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }}
      >

        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex-shrink-0">
            <Link to="/dashboard" className="flex items-center">
              <img 
                src="/images/mindmail-logo.png" 
                alt="MindMail Logo" 
                className="h-10 w-auto"
              />
            </Link>
          </div>
          
          {!isMobile && isHome && (
            <div className="flex-grow flex justify-center items-center">
              <h1 className="text-lg font-semibold text-foreground">Your Manifestation Toolkit</h1>
            </div>
          )}
          
          <div className="flex-shrink-0">
            {isMobile ? (
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-foreground hover:bg-muted rounded-full"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/">
                  <Button variant="ghost" className="animated-underline">
                    <Home className="mr-2 h-4 w-4" />
                    Home
                  </Button>
                </Link>
                <Link to="/subscription">
                  <Button className="cosmic-button">
                    Upgrade to Premium
                  </Button>
                </Link>
                {location.pathname !== "/dashboard/pair-device" && (
                  <SpotifyMusicPanel position="header" />
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="rounded-full ring-2 ring-border hover:ring-primary transition-all">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={userAvatar ?? undefined} alt="Account" />
                        <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                          {userEmail ? userEmail[0].toUpperCase() : <User className="h-4 w-4" />}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="font-normal">
                      <p className="text-sm font-medium">{userEmail ?? "Account"}</p>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/dashboard/pair-device")} className="cursor-pointer">
                      <Smartphone className="mr-2 h-4 w-4" />
                      Pair a device
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleSignOut} className="text-destructive cursor-pointer">
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </div>
        
        {isMobile && mobileMenuOpen && (
          <div className="container mx-auto px-4 py-4 flex flex-col space-y-2 bg-background/95 backdrop-blur-md border-t border-border">
            {location.pathname !== "/dashboard/pair-device" && (
              <div className="flex justify-start">
                <SpotifyMusicPanel position="header" />
              </div>
            )}
            <Link to="/" className="text-foreground hover:text-foreground/80 py-2 flex items-center" onClick={() => setMobileMenuOpen(false)}>
              <Home className="mr-2 h-4 w-4" />
              Home
            </Link>
            <Link to="/subscription" className="text-foreground hover:text-foreground/80 py-2" onClick={() => setMobileMenuOpen(false)}>
              Upgrade to Premium
            </Link>
            <Link to="/dashboard/pair-device" className="text-foreground hover:text-foreground/80 py-2 flex items-center" onClick={() => setMobileMenuOpen(false)}>
              <Smartphone className="mr-2 h-4 w-4" />
              Pair a device
            </Link>
            <button onClick={handleSignOut} className="text-foreground hover:text-foreground/80 py-2 flex items-center text-sm">
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </button>
          </div>
        )}
      </header>

      <div className="flex-1 overflow-y-auto flex flex-col">
        <Outlet />
      </div>
    </div>
  );
};

export default Dashboard;
